import React, { useCallback, useEffect, useState } from 'react';
import LinkPlus from '../UI/LinkPlus.js';
import MensajeCargando from '../UI/MensajeCargando.js';
import MensajeError from '../UI/MensajeError.js';
import CuadroColapsable from '../UI/CuadroColapsable.js';
import useGetListaURL from '../../hooks/useGetListaURL.js';
import styles from './CuadroDivisionArgentina.module.css';

const ID_ARGENTINA = 1;

/**
 * Muestra los participantes del torneo más reciente de una división argentina.
 * - Si debeExpandirseAutomaticamente es true: se carga y expande al montar.
 * - Si es false: arranca cerrado; carga los datos solo al expandir por primera vez.
 * Props:
 *   division  – objeto { id, descripcion, nivelActual }
 *   debeExpandirseAutomaticamente – boolean que indica si se debe expandir automáticamente
 */
const CuadroDivisionArgentina = ({ division, debeExpandirseAutomaticamente }) => {
  const cargaAutomatica = debeExpandirseAutomaticamente ?? false;

  const [cargado, setCargado] = useState(false);
  const [participantes, setParticipantes] = useState(null);
  const [tituloTorneo, setTituloTorneo] = useState('');

  const {
    isCargando: cargandoTorneos,
    erroresGetLista: errTorneos,
    fetchData: fetchTorneos,
  } = useGetListaURL();

  const {
    isCargando: cargandoPart,
    erroresGetLista: errPart,
    fetchData: fetchPart,
  } = useGetListaURL();

  const cargarDatos = useCallback(() => {
    if (cargado || !division || !division.id) return;
    setCargado(true);
    fetchTorneos('paises/' + ID_ARGENTINA + '/torneos?division=' + division.id, (torneos) => {
      if (!torneos || torneos.length === 0) {
        setParticipantes([]);
        setTituloTorneo('');
        return;
      }
      const masReciente = torneos.reduce((prev, curr) => {
        if (curr.anioInicio > prev.anioInicio) return curr;
        if (curr.anioInicio === prev.anioInicio && curr.id > prev.id) return curr;
        return prev;
      });
      setTituloTorneo(masReciente.nombre || String(masReciente.temporada));
      fetchPart('torneos/' + masReciente.id + '/participantes', (equipos) => {
        const ordenados = [...(equipos || [])].sort((a, b) =>
          (a.nombre || '').localeCompare(b.nombre || '', 'es')
        );
        setParticipantes(ordenados);
      });
    });
  }, [cargado, division, fetchTorneos, fetchPart]);

  // Carga automática para niveles 1-3
  useEffect(() => {
    if (cargaAutomatica) {
      cargarDatos();
    }
  }, [cargaAutomatica, cargarDatos]);

  const onExpandChange = (expandido) => {
    if (expandido && !cargado) {
      cargarDatos();
    }
  };

  const isCargando = cargandoTorneos || cargandoPart;
  const error = errTorneos || errPart;

  return (
    <CuadroColapsable
      titulo={division.descripcion}
      expandidoPorDefecto={cargaAutomatica}
      onExpandChange={onExpandChange}
    >
      <MensajeCargando isCargando={isCargando} />
      <MensajeError errores={error} />
      {tituloTorneo && <div className={styles.subtituloTorneo}>{tituloTorneo}</div>}
      {participantes && participantes.length === 0 && !isCargando && (
        <div className={styles.sinDatos}>Sin participantes</div>
      )}
      {participantes && participantes.length > 0 && (
        <ul className={styles.listaEquipos}>
          {participantes.map((participante) => {
            const equipo = participante.equipo;
            const primerClub =
              participante.clubes && participante.clubes.length > 0 ? participante.clubes[0] : null;
            const to = primerClub ? '/club/' + primerClub.id : '/equipo/' + equipo.id;
            return (
              <li key={equipo.id} className={styles.itemEquipo}>
                <LinkPlus to={to} className={styles.linkEquipo} adaptativo={true}>
                  {equipo.nombre}
                </LinkPlus>
              </li>
            );
          })}
        </ul>
      )}
    </CuadroColapsable>
  );
};

export default CuadroDivisionArgentina;
